import { FinalInvoiceStatus } from '@arhs-fleetback/types/entities/interventions';

import { isBefore, isEqual } from 'date-fns';
import { createSelector, weakMapMemoize } from 'reselect';

import { ContactType } from 'components/interventions/detail/forms/ContactInformationForm/constants';
import * as Utils from 'components/interventions/detail/utils/InterventionDisplayUtils';
import { getPrices } from 'components/interventions/detail/Works/utils';
import { ADVICE_STATUS, CHECKLIST_DONE, SALES_SEVERITY, SEVERITY, CHECKLIST_STATUS } from 'constants/ChecklistConstants';
import { UTC } from 'constants/Date';
import { CHAT, TRACK, FILES_EXTENSIONS_WO_SELF_SERVICE } from 'constants/ExtensionConstants';
import { PLATINUM, SIGNATURE_STATUS } from 'constants/Global';
import { ALL_STATUS, MODE_ENTITY, STATUSES, CLOSED, CUSTOMER_INFO_UPDATED, ADDITIONAL_CUSTOMER_INFO_UPDATED } from 'constants/InterventionConstants';
import { DOCUMENT_TYPE, IMAGE_TYPE, VIDEO_360_TYPE, VIDEO_TYPE } from 'constants/MediaConstants';
import { PARTS, SALES, TECHNICIAN } from 'constants/ModeConstants';
import { BODYWORK_APPOINTMENT,
  DEFAULT_SALES_MODULES,
  getDefaultModulesByMode,
  GUARANTEE,
  MODULES,
  STOCK_VO,
  SALES_MESSAGE,
  REMINDER,
  PARTS_REMINDER } from 'constants/ModuleConstants';
import { SELF_SERVICE_TYPE, CUSTOMER_UPDATE_TYPES, SELF_SERVICE_KEY_STATUS } from 'constants/SelfServiceConstants';
import { USER_TYPE } from 'constants/UserConstants';
import { isAppointmentSending } from 'modules/appointments/selectors/details';
import { StepsCodeEnum } from 'modules/global/typing/enums/steps.enum';
import { DEFAULT_PAYER_ID } from 'modules/payers/constants';
import { getPayerById } from 'modules/payers/selectors';
import { canDisplayTags } from 'modules/tags/selectors';
import { getCommunicationBlockingTags } from 'modules/tags/utils';
import { getCurrentUserIsAdmin,
  getCurrentUserIsReadOnly,
  getCurrentUserRoles,
  hasInterventionRole,
  currentUserHasSelfServiceOnly } from 'modules/users/selectors/currentUser';
import { getCurrentDealerFollowUpConfig,
  getDealerById,
  getSelectedDealer,
  getTimeZoneOffsetByDealerId,
  hasExtensions,
  hasPlannedInterventions,
  getValueAddedTax,
  hasAccessToDealer,
  isAllowSalesLead } from 'selectors/dealerSelectors';
import { getAdvices, getAnsweredAdvices, getAnsweredChecklistGroup } from 'services/ChecklistUtils';
import { isElementBefore } from 'utils/ArrayUtils';
import { convertTimeZones, tryParse } from 'utils/date';
import { getProps, getRoot } from 'utils/selectors';

import { getUIState,
  isRebrandingSelector,
  getByIds as getInterventionByIds,
  getSelectedIntervention,
  getDataEntities,
  getSelectedInterventionId,
  getById } from './base';
import { isStatusBelow } from '../utils/statusUtils';

const getIds = (_, ids) => ids;

const getSecondProps = (_, __, props) => props;

export const getUIDetailsState = createSelector(getUIState, ui => ui.details);

const isPrinting = createSelector(getUIDetailsState, state => state.printing);

const isIntegrating = createSelector(getUIDetailsState, state => state.integrating);

const isEvaluateTags = createSelector(getUIState, state => state.loading.evaluateTags);

export const getDealerIdByInterventionId = createSelector(
  getById,
  intervention => intervention?.dealer?.id ?? intervention?.dealerId
);

export const getDealerByInterventionId = createSelector(getRoot, getDealerIdByInterventionId, getDealerById);

export const getDealerTimeZoneOffsetByInterventionId = createSelector(
  getRoot,
  getDealerIdByInterventionId,
  getTimeZoneOffsetByDealerId
);

export const getInterventionMode = createSelector(getById, intervention => intervention?.mode);

export const getInterventionModule = createSelector(getById, intervention => intervention?.module);

export const isInterventionFetching = createSelector(getUIDetailsState, state => state.isFetching);

export const getInterventionApiError = createSelector(getUIDetailsState, state => state.apiError);

export const moreButtonIsLoading = createSelector(
  isPrinting,
  isIntegrating,
  isEvaluateTags,
  isRebrandingSelector,
  (...args) => args.some(Boolean),
);

export const getSelectedInterventionMode = createSelector(getSelectedIntervention, ({ mode }) => mode);

const getKioskPreferenceState = createSelector(getUIDetailsState, state => state.kioskPreference);

export const getKioskPreference = createSelector(getKioskPreferenceState, state => state.data);

export const kioskPreferenceLoading = createSelector(getKioskPreferenceState, state => state.loading);

const getShareState = createSelector(getUIDetailsState, state => state.share);

export const getShareLink = createSelector(getShareState, state => state.link);

export const isShareLinkLoading = createSelector(getShareState, state => state.loading);

export const getShareLinkApiError = createSelector(getShareState, state => state.apiError);

export const isInterventionSending = createSelector(
  getUIDetailsState,
  isAppointmentSending,
  (state, sendingAppointment) => state.sending || sendingAppointment
);

const getOtherInterventionsState = createSelector(getUIDetailsState, state => state.others);

const getOtherInterventionsIds = createSelector(
  getOtherInterventionsState,
  getProps,
  ({ ids }, excludedIds = []) =>
    (excludedIds.length === 0 ? ids : ids.filter(interventionId => !excludedIds.includes(interventionId))),
  { memoize: weakMapMemoize, memoizeOptions: { maxSize: 100 } }
);

export const haveOtherInterventions = createSelector(
  getOtherInterventionsState,
  getProps,
  ({ ids }, currentInterventionId) => ids.some(interventionId => interventionId !== currentInterventionId)
);

export const getOtherInterventionsData = createSelector(getRoot, getOtherInterventionsIds, getInterventionByIds, {
  memoize: weakMapMemoize,
});

export const isFetchingOtherInterventions = createSelector(getOtherInterventionsState, state => state.loading);

export const isImportWorksModalOpen = createSelector(
  getUIDetailsState,
  ({ worksImportModalVisible }) => worksImportModalVisible
);

export const isInterventionReady = createSelector(getUIDetailsState, state => state.ready);

export const isUpdating = createSelector(getUIDetailsState, state => state.isUpdating);

export const isDeleting = createSelector(getUIDetailsState, state => state.status.isDeleting);

export const isStatusPatching = createSelector(getUIDetailsState, state => state.status.isPatchingStatus);

export const getDetails = createSelector(getUIDetailsState, isInterventionFetching, (details, isFetching) => ({
  ...details,
  isFetching,
}));

export const getStepData = createSelector(getDetails, ({ step }) => step);

export const getInterventionDealerId = createSelector(
  getSelectedIntervention,
  intervention => intervention?.dealerId ?? intervention?.dealer?.id
);

export const getInterventionDealer = createSelector(getRoot, getInterventionDealerId, getDealerById);

const getInterventionParams = createSelector(
  getById,
  getSelectedIntervention,
  (interventionById, selectedIntervention) => interventionById ?? selectedIntervention
);

const canUpdateIntervention = (intervention, canAccess, isReadOnly, updating = false) => Utils.canSave(intervention, canAccess) && !updating && !isReadOnly;

const canAccessToInterventionDealer = createSelector(
  getRoot,
  getInterventionParams,
  (root, intervention) => hasAccessToDealer(root, intervention.dealerId ?? intervention.dealer?.id)
);

export const canSaveIntervention = createSelector(
  getInterventionParams,
  canAccessToInterventionDealer,
  getCurrentUserIsReadOnly,
  isUpdating,
  canUpdateIntervention
);

const hasPaymentEnabled = createSelector(
  getInterventionDealer,
  dealer => dealer?.config?.onlinePayment?.allowOnlinePayment ?? dealer?.allowOnlinePayment ?? false
);

export const additionalCustomerEnabledOnDealer = createSelector(
  getInterventionDealer,
  dealer => Boolean(dealer?.config?.selfService?.additionalCustomerEnabled)
);

export const hasAdditionalCustomer = createSelector(
  getSelectedIntervention,
  intervention => Boolean(intervention?.additionalCustomer)
);

export const enabledSecondaryContact = createSelector(
  getSelectedInterventionMode,
  additionalCustomerEnabledOnDealer,
  hasAdditionalCustomer,
  (mode, dealerEnabled, hasLegacy) => mode === TECHNICIAN && (dealerEnabled || hasLegacy)
);

export const getAdditionalChatConversationId = createSelector(
  getSelectedIntervention,
  intervention => intervention?.additionalConversationId
);

export const hasValidatedAdditionalCustomer = createSelector(
  getSelectedIntervention,
  intervention => Boolean(intervention?.additionalCustomer?.validated)
);

export const canDisplayAdditionalChat = createSelector(
  enabledSecondaryContact,
  hasValidatedAdditionalCustomer,
  getAdditionalChatConversationId,
  (enabled, validated, additionalConversationId) => enabled && validated && Boolean(additionalConversationId)
);

export const canEnablePayOnline = createSelector(
  getSelectedIntervention,
  hasPaymentEnabled,
  ({ module }, allowOnlinePayment) => allowOnlinePayment && MODULES[module]?.hasPayment
);

export const canDownloadLinkedFile = createSelector(
  getInterventionDealer,
  getSelectedInterventionMode,
  (dealer, mode) => {
    // BMWVID-18395 - Only available on AfterSales
    const isFrench = dealer?.country?.code?.toLowerCase() === 'fr' || dealer?.countryCode?.toLowerCase() === 'fr';
    return isFrench && mode === TECHNICIAN;
  }
);

export const isInterventionStockVO = createSelector(
  [getSelectedIntervention],
  intervention => intervention.mode === SALES && intervention.module === STOCK_VO
);

export const isAfterSales = createSelector(getSelectedInterventionMode, mode => mode === TECHNICIAN);

export const isSelectedBodywork = createSelector(getSelectedIntervention, intervention => intervention?.module === BODYWORK_APPOINTMENT);

export const isSelectedPartsIntervention = createSelector(getSelectedIntervention, intervention => intervention?.mode === PARTS);

export const isSelectedSalesIntervention = createSelector(
  getSelectedIntervention,
  getSecondProps,
  ({ module, mode } = {}, ignoreModes = false) =>
    mode === SALES && (ignoreModes || DEFAULT_SALES_MODULES.includes(module))
);

export const isAnswered = createSelector(getSelectedIntervention, ({ status }) => status === STATUSES.ANSWERED);

const hasTireReport = intervention => MODULES[intervention?.module]?.hasTireReport;

export const selectedInterventionHasTireReport = createSelector(getSelectedIntervention, hasTireReport);

export const canManageChecklistGroups = createSelector(
  canSaveIntervention,
  getSelectedIntervention,
  (canSave, { checklistGroups = [] }) =>
    canSave && !checklistGroups.some(({ status }) => status === CHECKLIST_STATUS.ACCEPTED)
);

export const getUnEmptyAnsweredChecklistGroups = createSelector(
  getSelectedIntervention,
  ({ checklists: allChecklists = [], checklistGroups = [] }) =>
    getAnsweredChecklistGroup(checklistGroups).filter(
      ({ id: checklistGroupId }) =>
        allChecklists.some(
          checklist =>
            checklist.checklistGroupId === checklistGroupId &&
            getAnsweredAdvices(checklist, { checklists: `checklists_${checklist.type}` }).length > 0
        ),
      []
    )
);

export const getInterventionDealerIdByIntervention = createSelector(
  getSelectedIntervention,
  intervention => intervention?.dealerId ?? intervention?.dealer?.id
);

// VAT
const getVat = (intervention, dealerVAT) => intervention?.valueAddedTax ?? dealerVAT;

export const getInterventionVAT = createSelector(getSelectedIntervention, getValueAddedTax, getVat);

export const getSelectedInterventionChecklists = createSelector(
  getSelectedIntervention,
  /** @return {import('modules/checklists/types/intervention-checklist').InterventionChecklist[]} */
  ({ checklists = [] }) => checklists
);

export const isReminderSelected = createSelector(
  getSelectedIntervention,
  ({ module }) => [REMINDER, PARTS_REMINDER].includes(module)
);

const canSendToCustomer = createSelector(
  isReminderSelected,
  getSelectedIntervention,
  (isReminder, { module, status, customerInfo = {} }) => {
    const hasCustomerInformation = Boolean(customerInfo.email || customerInfo.phoneNumber?.local);
    const isClosed = status === STATUSES.CLOSED;
    const toBeSent = status === STATUSES.TO_BE_SENT;
    const isDelayed = toBeSent && isReminder;
    const hasRightModule = module !== BODYWORK_APPOINTMENT;
    return !isDelayed && hasRightModule && !isClosed && (toBeSent || hasCustomerInformation);
  }
);

export const selectedInterventionHasNotSentAdvices = createSelector(
  canSendToCustomer,
  getSelectedInterventionChecklists,
  (canSend, checklists) => {
    if (!canSend) {
      return false;
    }
    const rightSeverities = [SEVERITY.LOW, SEVERITY.HIGH, SEVERITY.OFFERED, SALES_SEVERITY.PROPOSED];
    return getAdvices(checklists).some(({ severity, senderId, status: adviceStatus }) => {
      const hasRightSeverity = rightSeverities.includes(severity);
      const isPending = adviceStatus === ADVICE_STATUS.PENDING;
      return isPending && !senderId && hasRightSeverity;
    });
  }
);

export const selectedInterventionHasClosedStockWarning = createSelector(
  getSelectedIntervention,
  isAllowSalesLead,
  ({ lead }, allowSalesLead) => {
    const isDeletedOrClosedLead = lead?.status === STATUSES.CLOSED || lead?.status === STATUSES.DELETED;
    return allowSalesLead && isDeletedOrClosedLead;
  }
);

export const selectedInterventionHasDocumentRequiringSignature = createSelector(
  getSelectedIntervention,
  ({ documents }) =>
    Boolean(documents?.some((document) => document.status === SIGNATURE_STATUS.UNSIGNED))
);

export const selectedInterventionCanSendDocumentsToSign = createSelector(
  canSendToCustomer,
  selectedInterventionHasDocumentRequiringSignature,
  (canSend, hasDocuments) => canSend && hasDocuments
);

export const selectedInterventionHasNotSentFinalInvoice = createSelector(
  getSelectedIntervention,
  ({ finalInvoice }) => Boolean(finalInvoice) && finalInvoice.status === FinalInvoiceStatus.CREATED
);

const getSelectedInterventionTimeZoneOffset = createSelector(
  getRoot,
  getInterventionDealerId,
  getTimeZoneOffsetByDealerId
);

export const selectedInterventionHasExpired = createSelector(
  getSelectedIntervention,
  getSelectedInterventionTimeZoneOffset,
  ({ mode, module, timeToReply, status }, timeZone) => {
    const isParts = mode === PARTS;
    const isSalesMessage = module === SALES_MESSAGE;

    let isYesterday = false;
    const parsedTimeToReply = tryParse(timeToReply);

    const yesterday = convertTimeZones(new Date(), timeZone, UTC);
    if (parsedTimeToReply) {
      isYesterday = isBefore(parsedTimeToReply, yesterday) || isEqual(yesterday, parsedTimeToReply);
    }

    const hasOpenStatus = ![STATUSES.ANSWERED, STATUSES.CLOSED, STATUSES.DELETED].includes(status);
    return (isSalesMessage || isParts) && isYesterday && hasOpenStatus;
  }
);

export const canDelayIntervention = createSelector(
  getSelectedIntervention,
  isReminderSelected,
  ({ status }, isReminder) => {
    const hasRightStatus = [STATUSES.OPEN, STATUSES.TO_BE_SENT, STATUSES.IN_PROGRESS].includes(status);
    return isReminder && hasRightStatus;
  }
);

export const hasInterventionFullAccess = createSelector(
  getInterventionDealer,
  hasInterventionRole,
  (dealer, hasRightAccess) =>
    hasRightAccess && FILES_EXTENSIONS_WO_SELF_SERVICE.some(extension => dealer?.extensions?.includes(extension))
);

export const canDisplayMedias = createSelector(
  getSelectedIntervention,
  hasInterventionFullAccess,
  ({ medias, status }, hasAccess) =>
    hasAccess &&
    (medias?.filter(({ type }) => [IMAGE_TYPE, VIDEO_TYPE, VIDEO_360_TYPE].includes(type)).length > 0 ||
      status !== CLOSED)
);

export const canDisplayAttachments = createSelector(
  getSelectedIntervention,
  ({ medias, status }) => medias?.filter(({ type }) => type === DOCUMENT_TYPE).length > 0 || status !== CLOSED
);

export const canDisplayMediasSection = createSelector(
  canDisplayMedias,
  canDisplayAttachments,
  (displayMedias, displayAttachments) => displayMedias || displayAttachments
);

export const canDisplayDocumentsToSign = createSelector(
  getSelectedIntervention,
  currentUserHasSelfServiceOnly,
  ({ module }, isSelfServiceOnly) =>
    !isSelfServiceOnly && module !== BODYWORK_APPOINTMENT
);

const hasAvailableBranding = createSelector(
  getInterventionDealer,
  getSelectedIntervention,
  ({ config }, intervention) => {
    const brandId = intervention.brandId ?? intervention?.brand?.id;
    const { module, medias: interventionMedias = [], checklists = {} } = intervention;
    const advices = getAdvices(checklists);
    const allMedias = [...interventionMedias, ...advices.flatMap(({ medias = [] }) => medias)];

    return allMedias.some(
      ({ type }) =>
        (type === VIDEO_TYPE && config?.branding?.selectedVideoTemplates?.[module]?.[brandId]) ||
        (type === IMAGE_TYPE && config?.branding?.selectedImageTemplates?.[module]?.[brandId])
    );
  }
);

export const canBeAReminder = createSelector(getSelectedIntervention, ({ module, status, checklists = [] }) => {
  const ALLOWED_SEVERITIES = [SEVERITY.HIGH, SEVERITY.LOW, SALES_SEVERITY.PROPOSED];
  const ALLOWED_STATUSES = [ADVICE_STATUS.REFUSED, ADVICE_STATUS.PENDING];

  const hasAdvices = getAdvices(checklists).some(({ status: adviceStatus, severity }) => {
    const hasAllowedSeverity = ALLOWED_SEVERITIES.includes(severity);
    const hasAllowedStatus = ALLOWED_STATUSES.includes(adviceStatus);
    return hasAllowedSeverity && hasAllowedStatus;
  });

  return status !== STATUSES.CLOSED && MODULES[module]?.canCreateReminder && hasAdvices;
});

export const canCreateReminder = createSelector(
  getInterventionDealer,
  getSelectedInterventionMode,
  canBeAReminder,
  hasInterventionFullAccess,
  (dealer, mode, isTransformable, hasAccess) => {
    const field = MODE_ENTITY[mode];
    return hasAccess && Boolean(dealer?.config?.[field]?.reminder?.allowReminder) && isTransformable;
  }
);

export const hasReminder = createSelector(getSelectedIntervention, ({ reminders }) => reminders?.length > 0);

export const getSelectedInterventionModule = createSelector(getSelectedIntervention, ({ module }) => module);

export const hasAcceptedOffer = createSelector(getSelectedIntervention, ({ checklistGroups = [] }) =>
  checklistGroups.some(({ status }) => status === CHECKLIST_STATUS.ACCEPTED));

export const canCloseIntervention = createSelector(
  getSelectedIntervention,
  getCurrentUserIsReadOnly,
  ({ status }, isReadOnly) => !isReadOnly && status !== STATUSES.CLOSED
);

export const selectedInterventionIsClosed = createSelector(
  getSelectedIntervention,
  ({ status }) => status === STATUSES.CLOSED
);

export const canConvertToMessage = createSelector(
  getSelectedIntervention,
  ({ module, status }) => module === STOCK_VO && status === STATUSES.OPEN
);

export const canConvertToBodywork = createSelector(getSelectedIntervention, Utils.canConvertToBodywork);

export const canApproveManually = createSelector(
  getSelectedIntervention,
  hasInterventionFullAccess,
  (intervention, hasAccess) => Utils.canApproveManually(intervention) && hasAccess
);

export const canReopenIntervention = createSelector(getSelectedIntervention, getCurrentUserRoles, Utils.canReOpen);

export const canDeleteIntervention = createSelector(
  getSelectedIntervention,
  getCurrentUserRoles,
  ({ status }, roles) => status !== STATUSES.DELETED && Utils.canDelete(roles)
);

export const canRebrandMedias = createSelector(
  getSelectedIntervention,
  getInterventionDealer,
  hasAvailableBranding,
  canSaveIntervention,
  (intervention, dealer, hasBranding, editable) => {
    const hasPlatinumPack = dealer?.config?.pack?.fleetBackPack === PLATINUM;
    const hasMedia = Utils.hasGlobalImageOrVideos(intervention);

    return hasPlatinumPack && editable && hasBranding && hasMedia;
  }
);

const getAdvicePayerId = createSelector(getSelectedIntervention, getProps, (intervention, adviceId) => {
  if (!adviceId) {
    return undefined;
  }
  return getAdvices(intervention.checklists).find(advice => advice.adviceId === adviceId)?.payerId;
});

export const getAdvicePayer = createSelector(getRoot, getAdvicePayerId, getPayerById);

const getAdvicesFromChecklists = createSelector(getSelectedIntervention, ({ checklists = [] }) =>
  getAdvices(checklists));

const hasPaid = createSelector(getAdvicesFromChecklists, (advices = []) =>
  advices.some(({ status }) => [ADVICE_STATUS.PAID, ADVICE_STATUS.DEPOSIT_PAID].includes(status)));

export const displaySummaryPaymentColumn = createSelector(
  getSelectedIntervention,
  hasPaid,
  ({ enableOnlinePayment }, hasAdvicesPaid) => enableOnlinePayment || hasAdvicesPaid
);

export const displayInterventionDetailsDateSelectors = createSelector(
  isAfterSales,
  isReminderSelected,
  (isAfterSale, isReminder) => isAfterSale && !isReminder
);

const getCustomerInfoUpdatedEvents = createSelector(getSelectedIntervention, ({ events = [] } = {}) =>
  events.filter(({ event }) => event === CUSTOMER_INFO_UPDATED));

const getAdditionalCustomerOverrideEvents = createSelector(getSelectedIntervention, ({ events = [] } = {}) =>
  events.filter(({ event }) => event === ADDITIONAL_CUSTOMER_INFO_UPDATED));

const collectFieldsAsHighlightable = prefix => events => [
  ...new Set(events.flatMap(({ fields = [] }) => fields.map(field => `${prefix}${field.split('.')[0]}`)))
];

export const getCustomerInfoUpdatedFields = createSelector(getCustomerInfoUpdatedEvents, collectFieldsAsHighlightable(`${ContactType.CUSTOMER_INFO}.`));

export const getAdditionalCustomerUpdatedFields = createSelector(getAdditionalCustomerOverrideEvents, collectFieldsAsHighlightable(`${ContactType.ADDITIONAL_CUSTOMER_INFO}.`));

export const isLastEventContactInfoUpdated = createSelector(
  getSelectedIntervention,
  ({ events = [] } = {}) => events[0]?.event === CUSTOMER_INFO_UPDATED
);

export const hasMultipleCustomers = createSelector(
  getSelectedIntervention,
  ({ customerId, additionalCustomerId } = {}) => Boolean(customerId && additionalCustomerId)
);

export const getCustomerUpdateLatestType = createSelector(
  getCustomerInfoUpdatedEvents,
  events => events?.find(({ detail }) => CUSTOMER_UPDATE_TYPES.includes(detail))?.detail
);

const hasTracks = createSelector(getRoot, getInterventionDealerId, (state, dealerId) =>
  hasExtensions(state, dealerId, TRACK));

export const canUpdateSteps = createSelector(
  hasTracks,
  getCurrentUserIsReadOnly,
  (hasRightExtension, isReadOnly) => hasRightExtension && !isReadOnly
);

export const canUpdateTags = createSelector(
  getSelectedIntervention,
  getCurrentUserIsReadOnly,
  ({ status }, isReadOnly) => !isReadOnly && status !== STATUSES.CLOSED
);

export const canDisplaySelectedInterventionTags = createSelector(
  getRoot,
  getInterventionDealerId,
  getSelectedInterventionMode,
  canDisplayTags,
);

export const canRefreshSelectedInterventionTags = createSelector(
  getSelectedIntervention,
  canDisplaySelectedInterventionTags,
  ({ status }, allowed) => allowed && isStatusBelow(status, STATUSES.CLOSED)
);

const isBodyworkAppointment = createSelector(getById, intervention => intervention?.module === BODYWORK_APPOINTMENT);
export const canPreviewIntervention = createSelector(
  hasInterventionFullAccess,
  isBodyworkAppointment,
  (hasAccess, isBodyworkIntervention) => hasAccess && !isBodyworkIntervention
);

export const canFollowFile = createSelector(
  getCurrentUserIsAdmin,
  canSaveIntervention,
  (isAdmin, canSave) => !isAdmin && canSave
);

export const isFollowing = createSelector(getById, ({ following }) => following);

const hasChatExtension = createSelector(getRoot, getInterventionDealerId, (state, dealerId) =>
  hasExtensions(state, dealerId, CHAT));

export const forceInternalChatMessage = createSelector(
  getById,
  hasChatExtension,
  ({ module }, hasExtension) => !hasExtension && module === GUARANTEE
);

const isInterventionSentOrMore = createSelector(
  getById,
  ({ status } = {}) => !isElementBefore(ALL_STATUS, status, STATUSES.SENT)
);

export const canDisplayChatPanel = createSelector(
  getById,
  hasChatExtension,
  isInterventionSentOrMore,
  ({ module }, hasExtension, isSentOrMore) =>
    (MODULES?.[module]?.canAddMessage ?? false) && (hasExtension || (module === GUARANTEE && isSentOrMore))
);

export const canDisplayMessagePanel = createSelector(
  getById,
  hasInterventionFullAccess,
  ({ module }, hasAccess) => hasAccess && (MODULES?.[module]?.canAddMessage ?? false)
);

export const getPaymentsByInterventionId = createSelector(getById, ({ payments }) => payments ?? []);

const hasImportedFinalInvoice = intervention =>
  Object.keys(intervention?.finalInvoice?.externalIntegration ?? {}).length > 0;

export const isFinalInvoiceImportedByInterventionId = createSelector(getById, hasImportedFinalInvoice);

const hasFollowUp = createSelector(getSelectedInterventionModule, getSelectedDealer, (module, dealer) => {
  const { mode, hasFollowUp: allowFollowUp } = MODULES[module];
  const hasConfigStep = dealer?.config?.steps?.[mode]?.steps?.some(({ code }) => code === StepsCodeEnum.FOLLOW_UP) ?? false;
  return allowFollowUp && hasConfigStep;
});

export const shouldShowFollowUp = createSelector(hasFollowUp, hasInterventionFullAccess, (...args) =>
  args.every(Boolean));

export const getFollowUpAllowAutoSend = createSelector(
  getIds,
  getSelectedInterventionModule,
  getCurrentDealerFollowUpConfig,
  (brandId, module, followUpConfig) =>
    followUpConfig?.selectedTemplates?.[module]?.[brandId]?.allowAutoSend ??
    followUpConfig?.selectedTemplates?.[module]?.DEFAULT?.allowAutoSend
);

/**
 * Filter advices to only include those for the customer payer.
 * Advices with no payerId or with payerId === DEFAULT_PAYER_ID are for the customer.
 * @param {Array<import("modules/checklists/types/working/checklist-advice-form").ChecklistAdviceForm>} advices
 * @returns {Array<import("modules/checklists/types/working/checklist-advice-form").ChecklistAdviceForm>}
 */
const filterCustomerAdvices = advices =>
  advices.filter(({ payerId }) => !payerId || payerId === DEFAULT_PAYER_ID);

const getRemainingAmount = ({ checklists = [], payments, valueAddedTax, selfServices = [] }) => {
  const checkIn = selfServices.find(({ type }) => type === SELF_SERVICE_TYPE.CHECK_IN);

  const doneAdvices = filterCustomerAdvices(getAdvices(checklists, ({ type }) => type === CHECKLIST_DONE));
  const proposedAdvices = getAdvices(checklists, ({ type }) => type !== CHECKLIST_DONE);
  const extras = getAdvices(checkIn?.checklists ?? {});
  const allProposedAdvices = [...proposedAdvices, ...extras];

  const { remainingAmountWithVAT } = getPrices(allProposedAdvices, valueAddedTax, payments, [
    ADVICE_STATUS.PENDING,
    ADVICE_STATUS.REFUSED,
  ]);
  const { remainingAmountWithVAT: doneRemainingAmountWithVAT } = getPrices(doneAdvices, valueAddedTax);
  return remainingAmountWithVAT + doneRemainingAmountWithVAT;
};

export const getRemainingAmountByInterventionId = createSelector(getById, getRemainingAmount);

export const canDisplayFinalInvoice = createSelector(getSelectedIntervention, ({ module, mode }) => {
  const defaultModules = getDefaultModulesByMode(mode) ?? [];
  return mode !== SALES && defaultModules.includes(module);
});

/**
 * @typedef {{
 *  meta : {
 *     checklistId: string,
 *     categoryId: string,
 *     excludedAdvices: Array<string>,
 *     type: import("@arhs-fleetback/types/entities/checklists").ChecklistType,
 * }} & import('modules/checklists/types/resources/advices/intervention-checklist-advice').InterventionChecklistAdvice} AdviceWithParent
 */
export const getRefusedAdvices = createSelector(
  getDataEntities,
  getIds,
  (interventions, { interventionId, checklistId, excludedAdvices = [] }) => {
    /** @type {import('modules/checklists/types/intervention-checklist').InterventionChecklist[]} */
    const checklists = interventions[interventionId]?.checklists ?? [];

    /** @type {AdviceWithParent[]} */
    const interventionAdvices = checklists
      .filter(({ id }) => id === checklistId)
      .flatMap(({ id, type, items = [] }) =>
        items.flatMap(({ categoryId, categoryName, advices = [] }) =>
          advices
            .filter(({ reproposable, adviceId }) => reproposable && !excludedAdvices.includes(adviceId))
            .map(advice => ({ ...advice, meta: { id, categoryId, categoryName, type } }))));

    return interventionAdvices.filter(({ status }) => status === ADVICE_STATUS.REFUSED);
  }
);

export const hasRefusedAdvices = createSelector(getRefusedAdvices, advices => advices.length > 0);

export const getInterventionClipping = createSelector(
  getInterventionDealer,
  getSelectedInterventionMode,
  (dealer, mode) => dealer?.config?.[MODE_ENTITY[mode]]?.clipping?.enabled ?? false
);

const getInterventionMedias = createSelector(getSelectedIntervention, intervention => intervention.medias ?? []);
export const getInterventionGuidelines = createSelector(getInterventionMedias, medias =>
  medias.filter(media => media.guidelineItemId));

export const isSalesIntervention = createSelector(
  getById,
  (_, __, props) => props,
  ({ module, mode } = {}, ignoreModes = false) =>
    mode === SALES && (ignoreModes || DEFAULT_SALES_MODULES.includes(module))
);

export const isSalesOrPartsIntervention = createSelector(getById, ({ mode } = {}) => mode === SALES || mode === PARTS);

export const isPartsIntervention = createSelector(getById, ({ mode } = {}) => mode === PARTS);

export const isNotifiableIntervention = createSelector(getInterventionModule, module => module !== STOCK_VO);

export const canShowZeroPricesWarning = createSelector(
  getById,
  ({ displayItemsWithoutPrice, checklists = [] }) =>
    !displayItemsWithoutPrice &&
    getAdvices(checklists).some(
      ({ price, severity }) => price === 0 && [SEVERITY.HIGH, SEVERITY.LOW, SALES_SEVERITY.PROPOSED].includes(severity)
    )
);

export const isPlannedInterventionById = createSelector(getRoot, getDealerIdByInterventionId, hasPlannedInterventions);
export const isSelectedPlannedIntervention = createSelector(getRoot, getSelectedInterventionId, isPlannedInterventionById);

const haveMultiPayersEnabled = createSelector(
  getInterventionDealer,
  dealer => dealer?.config?.general?.multiplePayers?.enabled
);

export const getInterventionPayers = createSelector(
  getInterventionDealer,
  haveMultiPayersEnabled,
  (dealer, hasMultiPayers) => (hasMultiPayers ? dealer?.assignments?.payers : [])
);

export const isIntegrationAuthor = createSelector(
  getById,
  ({ createdBy }) => Boolean(createdBy) && (createdBy.integration || createdBy.type === USER_TYPE.SYSTEM)
);

export const getCurrentInterventionTags = createSelector(getDetails, ({ tags }) => tags);

export const getTagsBlockingOutgoingCommunication = createSelector(
  getCurrentInterventionTags,
  getCommunicationBlockingTags
);

export const hasFeatureActivatedAndValidatedAdditionalCustomer = createSelector(
  additionalCustomerEnabledOnDealer,
  canDisplayAdditionalChat,
  (hasFeature, additionalChat) => hasFeature && additionalChat
);

const getSelfServices = createSelector(getSelectedIntervention, (intervention) => intervention?.selfServices || []);

export const hasKeysInKiosk = createSelector(getSelfServices, (selfServices) => selfServices.some(
  selfService => selfService?.keyStatus === SELF_SERVICE_KEY_STATUS.IN_SLOT
));
